import React, { useState } from 'react';
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Button,
  Paper,
  Snackbar,
  Alert,
} from '@mui/material';
import { STATUSES, STATUS_TRANSITIONS } from '../../config/constants';
import { updateStudentStatus } from '../../services/studentService';
import StatusBadge from '../common/StatusBadge';

const BANKER_ALLOWED_STATUSES = [
  STATUSES.UNDER_REVIEW,
  STATUSES.QUERY_RAISED,
  STATUSES.APPROVED,
  STATUSES.REJECTED,
  STATUSES.SANCTIONED,
  STATUSES.DISBURSED,
];

const BankerStatusUpdate = ({ student, onStatusUpdate }) => {
  const [selectedStatus, setSelectedStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const availableStatuses = BANKER_ALLOWED_STATUSES.filter(
    (s) => (STATUS_TRANSITIONS[student?.status] || []).includes(s)
  );

  const handleSubmit = async () => {
    if (!selectedStatus) return;

    try {
      setLoading(true);
      await updateStudentStatus(student._id, { status: selectedStatus, notes });
      setSnackbar({
        open: true,
        message: `Status updated to "${selectedStatus}" successfully`,
        severity: 'success',
      });
      setSelectedStatus('');
      setNotes('');
      if (onStatusUpdate) {
        onStatusUpdate(selectedStatus);
      }
    } catch (err) {
      const errMsg = err.response?.data?.message || 'Failed to update status';
      setSnackbar({
        open: true,
        message: errMsg,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Update Status
      </Typography>

      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Current Status:
        </Typography>
        <StatusBadge status={student?.status} />
      </Box>

      <Typography variant="subtitle2" gutterBottom>
        Select new status:
      </Typography>

      {availableStatuses.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No transitions available from this status.
        </Typography>
      ) : (
        <RadioGroup
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          sx={{ mb: 2 }}
        >
          {availableStatuses.map((status) => (
            <FormControlLabel
              key={status}
              value={status}
              control={<Radio size="small" />}
              label={status}
            />
          ))}
        </RadioGroup>
      )}

      <TextField
        fullWidth
        multiline
        rows={3}
        label="Notes"
        placeholder="Add notes for this status change..."
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        variant="outlined"
        size="small"
        sx={{ mb: 2 }}
      />

      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={!selectedStatus || loading}
        sx={{ minWidth: 140 }}
      >
        {loading ? 'Updating...' : 'Update Status'}
      </Button>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default BankerStatusUpdate;
